import { encodeAbiParameters, keccak256, parseAbiParameters, zeroAddress, type Address } from "viem";
import type { z } from "zod";
import type { BootstrapResponse } from "../api/schemas";
import { savedMerkleV4ConfigSchema } from "../api/merkle-v4-schema";

const poolManager = "0xE2e105d3F7209A9DA11f83cCf3E7398a753823F1" as Address;
const token = "0x7aA1E7cA2AC9329aC4E19523a57C34Bf449a6B4E" as Address;
const hook = "0xE9a661077d1A0fEc2A4FcF4E510a2a220D7480cC" as Address;
/** Historical deployment for saved pre-bootstrap transactions and isolated fixtures only. */
export const MERKLE_V4 = Object.freeze({
  chainId: 99901, deploymentBlock: 273425n, environment: "production",
  token, poolManager,
  // Recovery/API pool address means the emitting manager, never the bytes32 pool ID.
  pool: poolManager,
  poolId: "0xc1bc9c11d8412d915208133134eb6f2b70c6a321e8860936b8f066a7dc21a376" as const,
  poolKey: { currency0: zeroAddress, currency1: token, fee: 0, tickSpacing: 1, hooks: hook } as const,
  hook, router: "0x1B341FDD55d56F8E08409A2C25DDFad93Eb03C1a" as Address,
  vault: "0x1c55E07cEc24Fae3126cdfA4947675BecEB223b8" as Address,
  stateView: "0x27eD73aE0652fdCf71d6793412A7525f32bE9dfE" as Address,
  permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3" as Address,
  routerHash: "0xa3b1ff66d689e45ddfb2f0ba861e5e681a476be3ced9c7376f1952c478dc9f53" as const,
  permit2Hash: "0xecc8810a534829aab9326a3f868a21ed05f1c62b57dcc82429c8a78b3a27cfdc" as const,
});
export type MerkleV4Config = Readonly<Omit<z.infer<typeof savedMerkleV4ConfigSchema>, "deploymentBlock"> & { deploymentBlock: bigint }>;
export const merkleV4Environment = `${MERKLE_V4.environment}:merkle-v4:${MERKLE_V4.poolId}`;

const same = (a: string | null | undefined, b: string | null | undefined) => typeof a === "string" && typeof b === "string" && a.toLowerCase() === b.toLowerCase();
const permit2RuntimeHash = MERKLE_V4.permit2Hash;
export function assertMerkleV4Config(config: MerkleV4Config): void {
  savedMerkleV4ConfigSchema.parse({ ...config, deploymentBlock: config.deploymentBlock.toString() });
  const key = config.poolKey;
  const id = keccak256(encodeAbiParameters(parseAbiParameters("address,address,uint24,int24,address"),
    [key.currency0, key.currency1, key.fee, key.tickSpacing, key.hooks]));
  if (config.deploymentBlock < 0n || config.deploymentBlock > BigInt(Number.MAX_SAFE_INTEGER) ||
    !same(config.pool, config.poolManager) || !same(key.currency0, zeroAddress) || !same(key.currency1, config.token) ||
    !same(key.hooks, config.hook) || key.fee !== 0 || key.tickSpacing !== 1 || !same(id, config.poolId) ||
    !same(config.permit2Hash, permit2RuntimeHash) ||
    [config.token, config.poolManager, config.router, config.hook, config.vault, config.stateView, config.permit2].some(a => same(a, zeroAddress)))
    throw new Error("MERKLE_V4_DEPLOYMENT_MISMATCH");
}

/** This validates bootstrap identity, not mint readiness or executable liquidity. */
export function validateMerkleV4Bootstrap(bootstrap: BootstrapResponse): void {
  const { trade, contracts, deployment, tokens } = bootstrap;
  if (!trade || deployment.venue !== "MERKLE_V4" || !deployment.instanceId ||
    bootstrap.environment !== "production" || bootstrap.forkTestOnly ||
    [bootstrap.sourceChainId, bootstrap.siwe.chainId, contracts.mintChainId, deployment.sourceChainId,
      deployment.executionChainId].some(chain => chain !== trade.chainId)) throw new Error("MERKLE_V4_DEPLOYMENT_MISMATCH");
  if (trade.configurationStatus !== "COMPLETE") return;
  const token = tokens.find(item => same(item.tokenAddress, trade.tokenAddress));
  if (tokens.length !== 1 || !token || token.chainId !== trade.chainId || token.venue !== "MERKLE_V4" ||
    token.decimals !== trade.tokenDecimals || token.poolAddress !== null || !same(token.poolManager, trade.poolManagerAddress) ||
    !same(token.poolId, trade.poolId) || !token.poolKey || !trade.poolKey ||
    !same(token.poolKey.currency0, trade.poolKey.currency0) || !same(token.poolKey.currency1, trade.poolKey.currency1) ||
    !same(token.poolKey.hooks, trade.poolKey.hooks) || token.poolKey.fee !== trade.poolKey.fee || token.poolKey.tickSpacing !== trade.poolKey.tickSpacing ||
    !same(contracts.poolManagerAddress, trade.poolManagerAddress) || !same(contracts.universalRouterAddress, trade.universalRouterAddress) ||
    !same(contracts.permit2Address, trade.permit2Address) || !same(contracts.stateViewAddress, trade.stateViewAddress) ||
    !same(contracts.rewardVaultAddress, trade.rewardVaultAddress) || !same(contracts.feeHookAddress, trade.feeHookAddress) ||
    !same(contracts.positionManagerAddress, trade.positionManagerAddress) || !same(contracts.lpLockAddress, trade.lpLockAddress) ||
    contracts.quoterAddress !== trade.quote.quoterAddress ||
    !same(trade.approval.sell.tokenAddress, trade.tokenAddress) || !same(trade.approval.sell.erc20Spender, trade.permit2Address) ||
    !same(trade.approval.sell.permit2Spender, trade.universalRouterAddress) ||
    !same(trade.nativeCurrency.address, zeroAddress) || !same(trade.fees.feeCurrency, zeroAddress))
    throw new Error("MERKLE_V4_DEPLOYMENT_MISMATCH");
}

export function merkleV4ConfigFromBootstrap(bootstrap: BootstrapResponse | null): MerkleV4Config | null {
  if (!bootstrap?.trade || bootstrap.trade.configurationStatus !== "COMPLETE") return null;
  validateMerkleV4Bootstrap(bootstrap);
  const t = bootstrap.trade;
  if (t.missingConfiguration.length || t.poolCreatedBlock === null || !t.tokenAddress || !t.poolManagerAddress || !t.poolId ||
    !t.poolKey || !t.universalRouterAddress || !t.feeHookAddress || !t.rewardVaultAddress || !t.stateViewAddress ||
    !t.permit2Address || !t.routerRuntimeCodeHash || !t.positionManagerAddress || !t.lpLockAddress)
    throw new Error("MERKLE_V4_CONFIGURATION_INCOMPLETE");
  const config: MerkleV4Config = { chainId: t.chainId, environment: "production", deploymentBlock: BigInt(t.poolCreatedBlock),
    token: t.tokenAddress, poolManager: t.poolManagerAddress, pool: t.poolManagerAddress, poolId: t.poolId,
    poolKey: t.poolKey, router: t.universalRouterAddress, hook: t.feeHookAddress, vault: t.rewardVaultAddress,
    stateView: t.stateViewAddress, permit2: t.permit2Address, routerHash: t.routerRuntimeCodeHash, permit2Hash: permit2RuntimeHash };
  assertMerkleV4Config(config);
  return config;
}
